import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChevronRightIconComponent } from '../../../@icons/chevron-right.component';
import { CheckIconComponent } from '../../../@icons/check-icon.component';

/** Hauteur minimale par taille ; le contenu peut l'agrandir, jamais la réduire. */
const HEIGHTS: Record<number, string> = { 52: 'min-h-[52px]', 56: 'min-h-14', 64: 'min-h-16' };

/**
 * Ligne de liste Terreau (`vw-list-row`, RAPPORT.md section 4.2) : vignette de
 * 40 px, titre 16 px semi-gras, sous-titre 14 px, chevron quand la ligne mène
 * quelque part. Toute la ligne est un `<button>` (R8) : le lecteur d'écran lit
 * « titre, sous-titre, bouton » d'un seul geste.
 *
 * La vignette se projette avec l'attribut `vwListRowIcon` ; sans elle, la place
 * n'est pas réservée. `tone="warn"` signale un réglage à corriger (« Alertes
 * coupées »), `tone="danger"` une action destructrice. `checked` coche la ligne
 * retenue dans une liste de choix (langue), et le lecteur d'écran l'annonce.
 * `interactive=false` en fait une ligne d'information (le climat d'une
 * adresse) : un simple bloc, sans bouton ni chevron, que rien n'invite à toucher.
 *
 * Deux emplacements de plus, vides par défaut et sans place réservée :
 * - `vwListRowSubtitle` : un sous-titre composé (une `vw-tag` puis un texte),
 *   sous le titre, après `subtitle` ;
 * - `vwListRowTrailing` : ce qui se lit à droite avant le chevron (les 3 jours
 *   de la météo). Dans une ligne interactive, rien de cliquable : tout est
 *   dans le bouton de la ligne. Une action à part (« … ») va dans une ligne
 *   `interactive=false` ;
 *   Avec `trailingWraps`, elle passe sous le texte plutôt que de l'écraser ;
 * - `vwListRowAction` : une action à part posée à côté du bouton de la ligne,
 *   pas dedans (la croix d'une ligne qui mène ailleurs), cible de 44 px.
 * `dense` resserre le texte (titre 14 px, sous-titre 12 px) pour les lignes
 * de 52 px ; `thumb="bare"` pose la vignette projetée telle quelle, sans
 * pastille de 40 px (un pictogramme météo, la lune), `thumbSize="26"` la
 * réduit à 26 px. `tone="stale"` dit une
 * donnée qui n'est plus à jour : pastille et titre ambre ; `tone="neutral"`,
 * une pastille grise (« Plus là », « Actualiser » de la feuille de réponse) ;
 * `tone="link"`, un lien vert (« Cette quinzaine, quand tu as le temps ») ;
 * `tone="cold"`, un titre bleu pour le froid et l'eau (« Pluie mercredi »).
 * Usage : `<vw-list-row [title]="..." [subtitle]="..." (rowClick)="...">
 *            <vw-bell-icon vwListRowIcon /></vw-list-row>`
 */
@Component({
  selector: 'vw-list-row',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, ChevronRightIconComponent, CheckIconComponent],
  templateUrl: './list-row.component.html',
  styleUrl: './list-row.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ListRowComponent {
  /** Texte déjà traduit. */
  @Input() title = '';
  @Input() subtitle = '';
  /** Chevron : la ligne ouvre un écran ou une feuille. Sans lui, elle agit sur place. */
  @Input() chevron = true;
  /** Choix retenu dans une liste : coche verte à droite. */
  @Input() checked = false;
  @Input() tone: 'default' | 'warn' | 'danger' | 'stale' | 'neutral' | 'link' | 'cold' = 'default';
  /** Faux : la ligne informe et ne mène nulle part (ni bouton ni chevron). */
  @Input() interactive = true;
  /** 56 px par défaut, 64 px quand le sous-titre tient sur deux lignes, 52 px pour une ligne `dense`. */
  @Input() size: 52 | 56 | 64 = 56;
  /** Texte resserré : titre 14 px, sous-titre 12 px. */
  @Input() dense = false;
  /** `tint` : pastille ronde de 40 px aux couleurs du ton ; `bare` : la vignette projetée seule. */
  @Input() thumb: 'tint' | 'bare' = 'tint';
  /** Diamètre de la pastille `tint` : 40 px, ou 26 px pour un pictogramme de 18 px (« Pas à jour »). */
  @Input() thumbSize: 40 | 26 = 40;
  /**
   * La droite passe sous le texte quand il n'a plus 6 rem (texte agrandi) :
   * le titre garde deux lignes au plus au lieu de se casser mot à mot.
   */
  @Input() trailingWraps = false;
  @Output() rowClick = new EventEmitter<void>();

  get heightClass(): string {
    return HEIGHTS[this.size] ?? HEIGHTS[56];
  }

  get titleColor(): string {
    if (this.tone === 'danger') return 'text-vw-danger';
    if (this.tone === 'stale') return 'text-vw-warn-ink';
    if (this.tone === 'cold') return 'text-vw-cold';
    return this.tone === 'link' ? 'text-vw-primary-ink' : 'text-vw-ink';
  }
}
